import { useMemo, useState, type ReactElement } from "react";
import { Boxes, Gauge, Mail, type LucideIcon } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Button,
  ConfirmDialog,
  Modal,
  SettingsGroup,
  Skeleton,
  Spinner,
  StatusPill,
  Switch,
  cn,
} from "@playbookai/ui";
import {
  MODULE_REQUIRES,
  PRODUCT_NAME,
  moduleSwitchChanges,
} from "@playbookai/types";
import { MODULE_DISPLAY_NAMES } from "@playbookai/api-clients";
import { AdminSection } from "./AdminSection.js";
import { ME_QUERY_KEY, useAuthStore } from "../../auth/auth-store.js";
import { refreshSession } from "../../auth/session-api.js";
import {
  sameSession,
  scopeFromClaims,
  type SessionScope,
} from "../../auth/session-scope.js";
import { SETTINGS_MODULES } from "../../settings/roster.js";
import { ALL_MODULES } from "../../modules/index.js";
import { getRuntimeConfig, isModuleEnabled } from "../../config/runtime.js";
import { installableKeyForAuthKey } from "./module-dependencies.js";
import {
  TenantConfigApiError,
  getTenantConfiguration,
  setTenantModuleStatus,
  tenantConfigurationQueryKey,
} from "../../api/tenant-config-api.js";

/* Modules admin page (PB-42, lifted from the Workspace settings page PB-181).
   Tenant-wide module availability, and since PB-47 / AC-05 it is WRITABLE: the
   rows come from Auth's module catalogue and each switch persists through
   `PUT /api/auth/admin/tenant-configuration/modules/{moduleKey}`.

   Before AC-05 this page was read-only, on the premise that enablement was
   deployment configuration (config.js / K8s ConfigMap). That is only half true —
   the deployment decides which modules EXIST, per-tenant enablement decides
   which of them a tenant may use — so the switches now reflect Auth's
   per-tenant state rather than `claims.enabledModules`, which is the AND of the
   two and cannot be written back.

   Admin-only via the `/admin` parent route gate (RequireTenantAdmin, PB-48);
   the server remains the authoritative gate. */

/** Copy for the module toggle's failure modes, keyed on the server's code. */
const MODULE_ERROR_COPY: Record<string, string> = {
  INSUFFICIENT_PRIVILEGE:
    "You no longer have permission to change modules. Reload and sign in again.",
  ENTITY_NOT_FOUND: "This deployment does not carry that module.",
  UNAUTHENTICATED: "Your session expired. Reload and sign in again.",
  /* d2: the switches changed since this page read them, so the pair the
     dialog named is no longer the pair the server sees. */
  module_dependency_required:
    "Another module has to change with this one. Check the switches and try again.",
};

function moduleErrorCopy(error: unknown): string {
  const code = error instanceof TenantConfigApiError ? error.code : undefined;
  // Own-property check: a code of "constructor" would otherwise resolve to an
  // inherited function rather than missing.
  if (code !== undefined && Object.hasOwn(MODULE_ERROR_COPY, code)) {
    return MODULE_ERROR_COPY[code] as string;
  }
  return error instanceof Error
    ? error.message
    : "Could not change that module. Try again.";
}

/** KPI Manager's Auth catalogue key (its product id is `kpi`). */
const KPI_MANAGER_KEY = "kpi_manager";

/** Presentation metadata for a catalogue module, matched on the Auth key. */
function presentationFor(moduleKey: string):
  | {
      readonly name: string;
      readonly icon: LucideIcon;
      readonly accent?: string;
    }
  | undefined {
  /* KPI Manager is a module but not a launcher app, so the Settings roster
     does not carry it; it wears the KPI Manager Settings section's glyph. */
  if (moduleKey === KPI_MANAGER_KEY) {
    return { name: MODULE_DISPLAY_NAMES.kpi, icon: Gauge };
  }
  return SETTINGS_MODULES.find((m) => (m.authModuleKey ?? m.id) === moduleKey);
}

/**
 * Catalogue keys this page governs that are NOT product apps.
 *
 * The catalogue is deliberately wider than the product-module vocabularies in
 * `@playbookai/types`, and `/api/auth/me` serves its enabled set verbatim — so a
 * platform capability can be governed here, and gated on the same claim, without
 * pretending to be an app. Email is the first: `may this workspace send email`
 * is an administrator's decision and belongs beside the others, not buried in
 * the mail page.
 *
 * These carry their own presentation because `SETTINGS_MODULES` and
 * `ALL_MODULES` describe apps — things with a route, a manifest and a launcher
 * entry, none of which a capability has.
 */
const CAPABILITY_MODULES: Readonly<
  Record<string, { readonly name: string; readonly description: string }>
> = {
  email: {
    name: "Email",
    description: "Invitations, access-granted notices and export links.",
  },
};

/** Only show a control when this shell can honour it. Future or
 * presentation-only catalogue entries would be a tenant-wide switch that does
 * nothing, so they stay hidden, matching the meeting decision to keep only
 * usable apps here. KPI Manager is shown since ADR-0100: its switch now gates
 * its APIs, its Settings section and every KPI widget. */
function isControllableModule(moduleKey: string): boolean {
  // DPM is deliberately a route placeholder, not an app administrators can
  // offer to a workspace. The meeting decision removed it from this surface;
  // do not let a future catalogue seed turn the placeholder into a control.
  if (moduleKey === "dpm") return false;
  /* A capability has no manifest and no runtime-config entry, so the app test
     below would reject it — but this shell DOES honour it: the admin rail and
     the `/admin/communication` route both gate on the same enabled set. */
  if (Object.hasOwn(CAPABILITY_MODULES, moduleKey)) return true;
  const manifest = ALL_MODULES.find((module) => module.id === moduleKey);
  if (manifest !== undefined) return isModuleEnabled(manifest.id);
  /* An installable module with no launcher manifest (KPI Manager): offered
     when this deployment installed it. */
  const installable = installableKeyForAuthKey(moduleKey);
  return (
    installable !== null &&
    getRuntimeConfig().installedModules.includes(installable)
  );
}

/** Whether a module has a launcher entry, for the switch-off wording. */
function isLauncherApp(moduleKey: string): boolean {
  return ALL_MODULES.some((module) => module.id === moduleKey);
}

/** "A", "A and B", "A, B and C". */
function joinNames(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** The requirement pairs in words, for Learn more (d1, d2). */
const REQUIREMENT_SENTENCES: readonly string[] = Object.entries(
  MODULE_REQUIRES,
).map(
  ([module, required]) =>
    `${MODULE_DISPLAY_NAMES[module as keyof typeof MODULE_DISPLAY_NAMES]} needs ${joinNames(
      (required ?? []).map((key) => MODULE_DISPLAY_NAMES[key]),
    )}.`,
);

/** Whether this row is an app (launcher + routes) or a platform capability. */
function isCapability(moduleKey: string): boolean {
  return Object.hasOwn(CAPABILITY_MODULES, moduleKey);
}

interface ScopedModuleNotice {
  readonly scope: SessionScope;
  readonly moduleKey: string;
  readonly tone: "healthy" | "watch" | "critical";
  readonly title: string;
  readonly message: string;
}

/** A module the change moves too (d2), named for the dialog. */
interface PairedModule {
  readonly moduleKey: string;
  readonly moduleName: string;
}

/**
 * A change waiting for the administrator's yes. Every switch-off asks; a
 * switch-on asks only when it must turn another module on too (d2).
 */
interface ModuleConfirmation {
  readonly scope: SessionScope;
  readonly moduleKey: string;
  readonly moduleName: string;
  readonly workspaceName: string;
  readonly status: "enabled" | "disabled";
  readonly paired: readonly PairedModule[];
}

interface ModuleChange {
  readonly scope: SessionScope;
  readonly moduleKey: string;
  readonly moduleName: string;
  readonly status: "enabled" | "disabled";
  /** The other modules moving with this one; empty for a single switch. */
  readonly paired: readonly PairedModule[];
}

export function ModulesAdmin(): ReactElement {
  const claims = useAuthStore((s) => s.claims);
  const tenantId = claims?.tenantId ?? null;
  const scope = scopeFromClaims(claims);
  const queryClient = useQueryClient();

  const tenantConfig = useQuery({
    queryKey: tenantConfigurationQueryKey(tenantId),
    queryFn: getTenantConfiguration,
  });

  const [learnMoreOpen, setLearnMoreOpen] = useState(false);
  const [confirmation, setConfirmation] = useState<ModuleConfirmation | null>(
    null,
  );
  const [notice, setNotice] = useState<ScopedModuleNotice | null>(null);
  const activeConfirmation =
    confirmation && sameSession(confirmation.scope, scope)
      ? confirmation
      : null;
  const visibleNotice =
    notice && sameSession(notice.scope, scope) ? notice : null;

  const toggleModule = useMutation({
    mutationFn: ({ moduleKey, status, paired }: ModuleChange) =>
      paired.length > 0
        ? setTenantModuleStatus(moduleKey, status, {
            includeDependencies: true,
          })
        : setTenantModuleStatus(moduleKey, status),
    onMutate: () => setNotice(null),
    onSuccess: async (configuration, variables) => {
      /* Seed from the server's own view rather than guessing optimistically.
         The originating scope travels with the mutation, so a session change
         cannot seed the newly active tenant's configuration cache. */
      queryClient.setQueryData(
        tenantConfigurationQueryKey(variables.scope?.tenantId ?? null),
        configuration,
      );
      /* Order matters. /api/auth/me serves enabledModules from the Redis session
         snapshot, which is only re-derived from Postgres inside the refresher —
         so invalidating the claims alone would very likely re-read the SAME
         stale module list and leave the nav inconsistent with what the BFF now
         enforces. Refresh the session first, then invalidate.

         The refresh is caught, and NOT allowed to reach `onError`. TanStack
         Query v5 runs onSuccess inside the mutation's own try block, so a throw
         here would route to onError and report "could not change that module" —
         after the write has already committed and the cache has already been
         seeded with the server's new configuration. That reads as a failed
         toggle when the toggle in fact succeeded, and would invite the admin to
         retry a write that already landed. Report the narrower truth instead:
         the change is saved, only the session did not re-derive. */
      let refreshFailed = false;
      try {
        await refreshSession();
      } catch {
        refreshFailed = true;
      }
      /* And reconcile the seed. Seeding alone can lose a race: only the row
         being written is disabled, so two DIFFERENT modules can be in flight at
         once, and `useMutation` does not serialize them — if the responses
         resolve out of order the older one is written last and the switch shows
         a state the server no longer holds. Nothing else refetches this key (the
         ME_QUERY_KEY invalidation targets /api/auth/me, not the configuration),
         so the refetch has to be here.

         The /me invalidation is FIRED, not awaited, and the configuration
         re-read is the only thing this mutation waits on. `invalidateQueries`
         resolves only once the refetch it triggers has SETTLED, and since
         PB-5206 gave /me a retry ladder that can be ~1.5s — or ~16.5s if the
         attempts hang at the 5s ceiling. Anything awaited here extends
         `isPending`, and THIS ROW IS DISABLED ON `isPending`, so awaiting /me
         would leave the admin unable to touch the switch they just flipped
         while a query whose answer this mutation never reads finishes failing.
         Same fire-and-forget shape SessionsModal already uses for this key.

         The ordering invariant is unaffected: it is refreshSession BEFORE the
         /me invalidation, and both still happen in that order. */
      void queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
      await queryClient.invalidateQueries({
        queryKey: tenantConfigurationQueryKey(
          variables.scope?.tenantId ?? null,
        ),
      });

      const liveScope = scopeFromClaims(useAuthStore.getState().claims);
      if (sameSession(variables.scope, liveScope)) {
        setNotice({
          scope: variables.scope,
          moduleKey: variables.moduleKey,
          tone: refreshFailed ? "watch" : "healthy",
          title: refreshFailed ? "Module saved" : "Availability updated",
          message: refreshFailed
            ? "The change is saved, but your session did not refresh. Reload the page so navigation matches."
            : `${joinNames([
                variables.moduleName,
                ...variables.paired.map((other) => other.moduleName),
              ])} ${variables.paired.length > 0 ? "are" : "is"} now ${variables.status === "enabled" ? "available" : "unavailable"} across this workspace.`,
        });
      }
      setConfirmation((current) =>
        current &&
        current.moduleKey === variables.moduleKey &&
        sameSession(current.scope, variables.scope)
          ? null
          : current,
      );
    },
    onError: (error: unknown, variables) => {
      /* A requirement refusal means this page's switches are stale: re-read
         them, so the next attempt names the pair the server will see. */
      if (
        error instanceof TenantConfigApiError &&
        error.code === "module_dependency_required"
      ) {
        void queryClient.invalidateQueries({
          queryKey: tenantConfigurationQueryKey(
            variables.scope?.tenantId ?? null,
          ),
        });
      }
      const liveScope = scopeFromClaims(useAuthStore.getState().claims);
      if (!sameSession(variables.scope, liveScope)) return;
      setNotice({
        scope: variables.scope,
        moduleKey: variables.moduleKey,
        tone: "critical",
        title: "Availability was not changed",
        message: moduleErrorCopy(error),
      });
    },
  });

  const modules = (tenantConfig.data?.modules ?? []).filter((module) =>
    isControllableModule(module.moduleKey),
  );
  /* Two groups from ONE catalogue read and ONE mutation. Apps and capabilities
     differ only in what turning them off means, which is what their group
     headings say — not in how they are stored or written, so splitting the data
     source or the write would be inventing a difference. */
  const appModules = modules.filter(
    (module) => !isCapability(module.moduleKey),
  );
  const capabilityModules = modules.filter((module) =>
    isCapability(module.moduleKey),
  );
  const confirmPending =
    toggleModule.isPending &&
    activeConfirmation !== null &&
    toggleModule.variables?.status === activeConfirmation.status &&
    toggleModule.variables.moduleKey === activeConfirmation.moduleKey &&
    sameSession(toggleModule.variables.scope, activeConfirmation.scope);

  const closeConfirmation = (): void => {
    if (confirmPending) return;
    setConfirmation(null);
    if (visibleNotice?.tone === "critical") setNotice(null);
  };

  /* The switches as STORED, which is what Auth judges a requirement against
     (d2) — not the session claim, which is already narrowed. */
  const storedEnabledKeys = useMemo(
    () =>
      (tenantConfig.data?.modules ?? [])
        .filter((module) => module.status === "enabled")
        .map((module) => module.moduleKey),
    [tenantConfig.data],
  );
  const nameFor = (moduleKey: string): string =>
    presentationFor(moduleKey)?.name ??
    CAPABILITY_MODULES[moduleKey]?.name ??
    tenantConfig.data?.modules.find((module) => module.moduleKey === moduleKey)
      ?.displayName ??
    moduleKey;

  /* One entry for every switch. A switch-off always asks first; a switch-on
     asks only when another module must come on with it (d2), and then sends
     `includeDependencies` so both move in one audited write. */
  const requestChange = (
    moduleKey: string,
    moduleName: string,
    next: boolean,
  ): void => {
    const status = next ? "enabled" : "disabled";
    const paired = moduleSwitchChanges(
      moduleKey,
      status,
      storedEnabledKeys,
    ).map((key) => ({ moduleKey: key, moduleName: nameFor(key) }));
    if (next && paired.length === 0) {
      toggleModule.mutate({ scope, moduleKey, moduleName, status, paired });
      return;
    }
    setNotice(null);
    setConfirmation({
      scope,
      moduleKey,
      moduleName,
      workspaceName: claims?.tenantName?.trim() || "this workspace",
      status,
      paired,
    });
  };

  const confirmingOn = activeConfirmation?.status === "enabled";
  const pairedNames = joinNames(
    activeConfirmation?.paired.map((other) => other.moduleName) ?? [],
  );
  const allNames = activeConfirmation
    ? joinNames([
        activeConfirmation.moduleName,
        ...activeConfirmation.paired.map((other) => other.moduleName),
      ])
    : "this module";
  /* d15: SOLVE access that comes only through a VSM pauses while VSM is off
     (d4). No count — the dialog says what happens, in plain words. */
  const turnsOffVsm =
    activeConfirmation !== null &&
    !confirmingOn &&
    (activeConfirmation.moduleKey === "vsm" ||
      activeConfirmation.paired.some((other) => other.moduleKey === "vsm"));

  return (
    <AdminSection
      icon={Boxes}
      title="Modules"
      description={`Control which ${PRODUCT_NAME} apps and workspace capabilities are available to everyone here.`}
      action={
        <Button variant="secondary" onClick={() => setLearnMoreOpen(true)}>
          Learn more
        </Button>
      }
      meta={null}
    >
      <SettingsGroup title="Available modules" titleHidden>
        <div className="flex flex-col gap-3">
          {visibleNotice &&
          !(
            activeConfirmation &&
            visibleNotice.moduleKey === activeConfirmation.moduleKey
          ) ? (
            <Alert tone={visibleNotice.tone} title={visibleNotice.title}>
              {visibleNotice.message}
            </Alert>
          ) : null}
          {tenantConfig.isError ? (
            <Alert
              tone="critical"
              title="Couldn't load the module availability"
            >
              <div className="flex flex-col items-start gap-3">
                <span>{(tenantConfig.error as Error | null)?.message}</span>
                <Button
                  variant="secondary"
                  onClick={() => {
                    void tenantConfig.refetch();
                  }}
                >
                  Retry
                </Button>
              </div>
            </Alert>
          ) : null}
          {tenantConfig.isPending ? (
            <div className="overflow-hidden rounded-xl border border-pb-border/80 bg-pb-card">
              {["one", "two", "three", "four"].map((row) => (
                <div
                  key={row}
                  className="flex items-center justify-between gap-4 border-b border-pb-border/70 px-4 py-4 last:border-b-0"
                >
                  <div className="flex items-center gap-3">
                    <Skeleton shape="panel" className="h-9 w-9" />
                    <Skeleton className="h-4 w-28" />
                  </div>
                  <Skeleton className="h-6 w-24" />
                </div>
              ))}
            </div>
          ) : null}
          {tenantConfig.data && modules.length === 0 ? (
            <Alert tone="info" title="No modules are available">
              This deployment does not currently include any apps that can be
              managed here.
            </Alert>
          ) : null}
          {appModules.length > 0 ? (
            <div className="overflow-hidden rounded-xl border border-pb-border/80 bg-pb-card">
              <div className="hidden grid-cols-[minmax(0,1fr)_auto] items-center gap-6 border-b border-pb-border/70 bg-pb-card-elevated/60 px-4 py-2.5 text-xs font-medium text-pb-text-muted sm:grid">
                <span>Module</span>
                <span className="pr-1">Availability</span>
              </div>
              <div className="divide-y divide-pb-border/70">
                {appModules.map((module) => {
                  const presentation = presentationFor(module.moduleKey);
                  const Icon = presentation?.icon ?? Boxes;
                  const moduleName = presentation?.name ?? module.displayName;
                  const pending =
                    toggleModule.isPending &&
                    toggleModule.variables?.moduleKey === module.moduleKey &&
                    sameSession(toggleModule.variables.scope, scope);
                  return (
                    <div
                      key={module.moduleKey}
                      className="grid gap-3 px-4 py-3.5 transition-colors hover:pb-overlay-paint sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center sm:gap-6"
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-pb-border/70 bg-pb-card-elevated">
                          <Icon
                            className={cn(
                              "h-4 w-4",
                              presentation?.accent ?? "text-pb-text-secondary",
                            )}
                            aria-hidden
                          />
                        </span>
                        <span className="truncate text-sm font-medium text-pb-text">
                          {moduleName}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-3 sm:justify-end">
                        <span
                          className="inline-flex min-h-4 items-center gap-1.5 text-xs text-pb-text-muted"
                          aria-live="polite"
                        >
                          {pending ? (
                            <>
                              {/* Look row OK-38: the Spinner ring. */}
                              <Spinner size="sm" aria-hidden />
                              Saving…
                            </>
                          ) : null}
                        </span>
                        <StatusPill
                          tone={
                            module.status === "enabled" ? "healthy" : "neutral"
                          }
                        >
                          {module.status === "enabled" ? "Enabled" : "Disabled"}
                        </StatusPill>
                        <Switch
                          aria-label={`${moduleName} module availability`}
                          checked={module.status === "enabled"}
                          disabled={pending}
                          onChange={(next: boolean) =>
                            requestChange(module.moduleKey, moduleName, next)
                          }
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      </SettingsGroup>

      {/* A SEPARATE group, deliberately, and below the apps.
        *
          Same catalogue, same write, same switch — but turning one of these off
          means something different, and the two headings are how an
          administrator can tell. The Learn more copy promises that a module off
          is "removed from the launcher and its direct links and APIs are
          blocked": true of every app above, and false of a capability, which
          has no launcher entry at all. */}
      {capabilityModules.length > 0 ? (
        <SettingsGroup
          title="Workspace capabilities"
          description="Platform features this workspace can use. Turning one off hides its settings and stops the feature; it is not an app, so nothing changes in the launcher."
        >
          <div className="overflow-hidden rounded-xl border border-pb-border/80 bg-pb-card">
            <div className="hidden grid-cols-[minmax(0,1fr)_auto] items-center gap-6 border-b border-pb-border/70 bg-pb-card-elevated/60 px-4 py-2.5 text-xs font-medium text-pb-text-muted sm:grid">
              <span>Capability</span>
              <span className="pr-1">Availability</span>
            </div>
            <div className="divide-y divide-pb-border/70">
              {capabilityModules.map((module) => {
                const capability = CAPABILITY_MODULES[module.moduleKey];
                const moduleName = capability?.name ?? module.displayName;
                const pending =
                  toggleModule.isPending &&
                  toggleModule.variables?.moduleKey === module.moduleKey &&
                  sameSession(toggleModule.variables.scope, scope);
                return (
                  <div
                    key={module.moduleKey}
                    className="grid gap-3 px-4 py-3.5 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center sm:gap-6"
                    data-testid={`capability-row-${module.moduleKey}`}
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-pb-border/70 bg-pb-card-elevated">
                        <Mail
                          className="h-4 w-4 text-pb-cyan-text"
                          aria-hidden
                        />
                      </span>
                      <div className="flex min-w-0 flex-col">
                        <span className="truncate text-sm font-medium text-pb-text">
                          {moduleName}
                        </span>
                        {capability?.description ? (
                          <span className="text-xs text-pb-text-muted">
                            {capability.description}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-3 sm:justify-end">
                      <span
                        className="inline-flex min-h-4 items-center gap-1.5 text-xs text-pb-text-muted"
                        aria-live="polite"
                      >
                        {pending ? (
                          <>
                            <Spinner size="sm" aria-hidden />
                            Saving…
                          </>
                        ) : null}
                      </span>
                      {/* The SAME two words the app rows use. This is a module
                          gate now, not a sending pause, so "Enabled"/"Disabled"
                          is the honest vocabulary — whether a tested mail server
                          exists is a separate readiness fact, reported on the
                          Email & notifications page. */}
                      <StatusPill
                        tone={
                          module.status === "enabled" ? "healthy" : "neutral"
                        }
                      >
                        {module.status === "enabled" ? "Enabled" : "Disabled"}
                      </StatusPill>
                      <Switch
                        aria-label={`${moduleName} availability`}
                        checked={module.status === "enabled"}
                        disabled={pending}
                        data-testid={`capability-toggle-${module.moduleKey}`}
                        onChange={(next: boolean) =>
                          requestChange(module.moduleKey, moduleName, next)
                        }
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </SettingsGroup>
      ) : null}

      <Modal
        open={learnMoreOpen}
        onClose={() => setLearnMoreOpen(false)}
        title="Managing module availability"
        description="How workspace-wide module access behaves."
        describedBy="module-availability-help"
        size="md"
        footer={
          <Button variant="secondary" onClick={() => setLearnMoreOpen(false)}>
            Done
          </Button>
        }
      >
        <div
          id="module-availability-help"
          className="space-y-4 text-sm text-pb-text-secondary"
        >
          <section>
            <h3 className="font-semibold text-pb-text">Who is affected</h3>
            <p className="mt-1">
              Availability applies to everyone in this workspace. Only modules
              included in this deployment can be managed here.
            </p>
          </section>
          <section>
            <h3 className="font-semibold text-pb-text">When a module is off</h3>
            <p className="mt-1">
              The module is removed from the launcher and its direct links and
              APIs are blocked. Existing module data is retained.
            </p>
          </section>
          <section>
            <h3 className="font-semibold text-pb-text">
              Modules that need another
            </h3>
            <p className="mt-1">
              {REQUIREMENT_SENTENCES.join(" ")} Turning on a module also turns
              on what it needs, and turning off a module also turns off what
              needs it. You are asked before both change.
            </p>
          </section>
          <section>
            <h3 className="font-semibold text-pb-text">When changes appear</h3>
            <p className="mt-1">
              New sessions reflect the change immediately. People who are
              already signed in may retain access until their session refreshes.
            </p>
          </section>
        </div>
      </Modal>

      {/* ConfirmDialog (d2): the destructive confirm, red chip, quiet Cancel,
          for a switch-off; the neutral one for a switch-on that must bring
          another module with it. `loading` blocks every dismissal while the
          write is in flight. */}
      <ConfirmDialog
        open={activeConfirmation !== null}
        onClose={closeConfirmation}
        tone={confirmingOn ? "neutral" : "critical"}
        title={`${confirmingOn ? "Turn on" : "Turn off"} ${allNames}?`}
        confirmLabel={`${confirmingOn ? "Turn on" : "Turn off"} ${
          activeConfirmation ? allNames : "module"
        }`}
        loading={confirmPending}
        onConfirm={() => {
          if (!activeConfirmation) return;
          setNotice(null);
          toggleModule.mutate({
            scope: activeConfirmation.scope,
            moduleKey: activeConfirmation.moduleKey,
            moduleName: activeConfirmation.moduleName,
            status: activeConfirmation.status,
            paired: activeConfirmation.paired,
          });
        }}
      >
        {/* The consequence depends on WHICH KIND of row this is, and sharing one
            body got it wrong: a capability is not in the launcher, has no direct
            links to block and no data to retain, so the app copy stated three
            things that are false of Email and omitted the one that matters —
            that nothing will be emailed. */}
        <div id="module-disable-impact" className="space-y-4">
          {activeConfirmation && isCapability(activeConfirmation.moduleKey) ? (
            <>
              <p className="text-sm text-pb-text">
                {PRODUCT_NAME} will stop sending email for everyone in{" "}
                {activeConfirmation.workspaceName}. Invitations, access-granted
                notices and export links will not be delivered, and its settings
                are hidden until it is turned back on.
              </p>
              <p className="text-sm text-pb-text-secondary">
                Your mail server configuration is kept. Anything that falls due
                while email is off is not sent later, even after you turn it
                back on.
              </p>
            </>
          ) : confirmingOn ? (
            <p className="text-sm text-pb-text">
              {activeConfirmation?.moduleName} needs {pairedNames}, so{" "}
              {allNames} will be turned on for everyone in{" "}
              {activeConfirmation?.workspaceName}.
            </p>
          ) : (
            <>
              <p className="text-sm text-pb-text">
                {activeConfirmation?.moduleName ?? "This module"} will be{" "}
                {activeConfirmation &&
                isLauncherApp(activeConfirmation.moduleKey)
                  ? "removed from the launcher"
                  : "hidden"}{" "}
                and unavailable through direct links for everyone in{" "}
                {activeConfirmation?.workspaceName ?? "this workspace"}.
              </p>
              {activeConfirmation && activeConfirmation.paired.length > 0 ? (
                <p className="text-sm text-pb-text">
                  {pairedNames}{" "}
                  {activeConfirmation.paired.length > 1 ? "need" : "needs"}{" "}
                  {activeConfirmation.moduleName}, so {pairedNames} will be
                  turned off too.
                </p>
              ) : null}
              {turnsOffVsm ? (
                <p className="text-sm text-pb-text">
                  People who see SOLVEs only through a VSM will lose that access
                  until VSM is back on. People with a SOLVE open may keep seeing
                  it until they reload.
                </p>
              ) : null}
              <p className="text-sm text-pb-text-secondary">
                Existing data will be retained. Some people who are already
                signed in may keep access until their session refreshes.
              </p>
            </>
          )}
          {visibleNotice?.tone === "critical" &&
          visibleNotice.moduleKey === activeConfirmation?.moduleKey ? (
            <Alert tone="critical" title={visibleNotice.title}>
              {visibleNotice.message}
            </Alert>
          ) : null}
        </div>
      </ConfirmDialog>
    </AdminSection>
  );
}
